// Census match review: vendor employees pulled from payroll, with per-row
// actions to confirm a match, create a new employee, ignore, or unlink.

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, UserCheck, UserPlus, EyeOff, Link2Off } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface CensusEmployeeRef {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
}

interface CensusLink {
  id: number;
  vendorEmployeeId: string;
  matchStatus: "unmatched" | "needs_review" | "auto_matched" | "linked" | "ignored";
  matchScore: number | null;
  canonical: {
    firstName: string;
    lastName: string;
    workEmail?: string;
    personalEmail?: string;
    dateOfBirth?: string;
    hireDate?: string;
    jobTitle?: string;
    status: string;
  };
  linkedEmployee: CensusEmployeeRef | null;
  candidates: Array<{ employeeId: number; score: number; reasons: string[]; employee: CensusEmployeeRef | null }>;
}

interface CensusResponse {
  links: CensusLink[];
  tallies: Record<string, number>;
}

const FILTERS = [
  { value: "review", label: "Needs review" },
  { value: "auto_matched", label: "Auto-matched" },
  { value: "linked", label: "Confirmed" },
  { value: "ignored", label: "Ignored" },
  { value: "all", label: "All" },
];

export default function PayrollMatchReview({ clientId }: { clientId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("review");
  const [chosenCandidate, setChosenCandidate] = useState<Record<number, number>>({});

  const censusKey = `/api/clients/${clientId}/payroll-census?status=${filter}`;
  const { data, isLoading } = useQuery<CensusResponse>({ queryKey: [censusKey] });

  const invalidateCensus = () =>
    queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith(`/api/clients/${clientId}/payroll-census`) });

  const ACTION_TITLES: Record<string, string> = {
    confirm: "Match confirmed",
    "create-employee": "Employee created and linked",
    ignore: "Record ignored",
    unlink: "Unlinked — back in review",
  };

  const actionMutation = useMutation({
    mutationFn: async ({ linkId, verb, body }: { linkId: number; verb: string; body?: Record<string, unknown> }) =>
      apiRequest("POST", `/api/payroll-census/${linkId}/${verb}`, body ?? {}),
    onSuccess: (_data, vars) => {
      invalidateCensus();
      toast({ title: ACTION_TITLES[vars.verb] ?? "Done", variant: "success" });
    },
    onError: (e: any) => toast({ variant: "destructive", title: "Action failed", description: e?.message }),
  });

  const tallies = data?.tallies ?? {};
  const reviewCount = (tallies.needs_review ?? 0) + (tallies.unmatched ?? 0);
  const links = data?.links ?? [];
  const busy = actionMutation.isPending;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Payroll census
          </CardTitle>
          <CardDescription className="mt-1">
            Employees pulled from the connected payroll provider, matched against this company's roster.
          </CardDescription>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2 text-sm">
            <Badge variant="secondary">{(tallies.auto_matched ?? 0) + (tallies.linked ?? 0)} matched</Badge>
            <Badge variant={reviewCount > 0 ? "destructive" : "secondary"}>{reviewCount} to review</Badge>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading census…</p>
        ) : links.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {filter === "review" ? "Nothing to review — all payroll records are resolved." : "No records."}
          </p>
        ) : (
          <div className="space-y-3">
            {links.map((link) => {
              const c = link.canonical;
              const needsAction = link.matchStatus === "needs_review" || link.matchStatus === "unmatched";
              const selectedCandidate =
                chosenCandidate[link.id] ?? link.candidates[0]?.employeeId ?? undefined;
              return (
                <div key={link.id} className="p-3 border rounded-lg flex flex-wrap items-center gap-3">
                  <div className="min-w-[200px] flex-1">
                    <p className="font-medium">
                      {c.firstName} {c.lastName}
                      {c.status === "terminated" && (
                        <Badge variant="outline" className="ml-2">Terminated</Badge>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {[c.workEmail || c.personalEmail, c.jobTitle, c.dateOfBirth && `DOB ${c.dateOfBirth}`, c.hireDate && `Hired ${c.hireDate}`]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    {link.linkedEmployee && (
                      <p className="text-sm text-muted-foreground">
                        → linked to{" "}
                        <span className="font-medium">
                          {link.linkedEmployee.firstName} {link.linkedEmployee.lastName}
                        </span>
                        {link.matchStatus === "auto_matched" && " (auto)"}
                      </p>
                    )}
                  </div>

                  {needsAction ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      {link.candidates.length > 0 && (
                        <Select
                          value={selectedCandidate != null ? String(selectedCandidate) : undefined}
                          onValueChange={(v) => setChosenCandidate({ ...chosenCandidate, [link.id]: Number(v) })}
                        >
                          <SelectTrigger className="w-[220px]">
                            <SelectValue placeholder="Choose employee…" />
                          </SelectTrigger>
                          <SelectContent>
                            {link.candidates.map((cand) => (
                              <SelectItem key={cand.employeeId} value={String(cand.employeeId)}>
                                {cand.employee
                                  ? `${cand.employee.firstName} ${cand.employee.lastName}`
                                  : `Employee #${cand.employeeId}`}{" "}
                                ({cand.reasons.join("+")})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {link.candidates.length > 0 && (
                        <Button
                          size="sm"
                          disabled={busy || selectedCandidate == null}
                          onClick={() => actionMutation.mutate({ linkId: link.id, verb: "confirm", body: { employeeId: selectedCandidate } })}
                        >
                          <UserCheck className="h-4 w-4 mr-1" />
                          Confirm
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => actionMutation.mutate({ linkId: link.id, verb: "create-employee" })}
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Create employee
                      </Button>
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => actionMutation.mutate({ linkId: link.id, verb: "ignore" })}>
                        <EyeOff className="h-4 w-4 mr-1" />
                        Ignore
                      </Button>
                    </div>
                  ) : link.matchStatus === "ignored" ? (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => actionMutation.mutate({ linkId: link.id, verb: "unlink" })}>
                      Restore
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => actionMutation.mutate({ linkId: link.id, verb: "unlink" })}>
                      <Link2Off className="h-4 w-4 mr-1" />
                      Unlink
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
